import React, { useState, useEffect } from 'react';
import { 
    Shield, ShieldAlert, ShieldCheck, AlertTriangle, Search, Filter, 
    CheckCircle2, XCircle, Ban, RefreshCw, Radio, Video, MessageSquare, 
    PhoneCall, ShoppingBag, Eye, Star, Users, ExternalLink, Calendar,
    Clock, ChevronRight, Check, X, FileText, Lock, Unlock, Store
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { isRTL } from '../utils/translations';
import { useAuth } from '../context/AuthContext';

export default function AdminSellerControlPage() {
    const rtl = isRTL();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [activeTab, setActiveTab] = useState('SELLERS'); // 'SELLERS' | 'REPORTS' | 'AUDIT_LOGS'
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [verificationFilter, setVerificationFilter] = useState('ALL');

    // Default Seed Sellers matching actual platform sellers
    const INITIAL_SELLERS = [
        {
            id: 'seller_ahmed_watches',
            slug: 'ahmed-watches',
            name: 'Ahmed Watches Co.',
            storeName: 'Ahmed Haute Horlogerie',
            ownerEmail: 'ahmed@watches.luxvono.com',
            rating: 4.8,
            reviewsCount: 324,
            followers: 12400,
            productsCount: 86,
            videosCount: 143,
            totalViews: 184200,
            ordersCount: 512,
            country: 'SA',
            status: 'ACTIVE', // 'ACTIVE', 'PENDING', 'SUSPENDED', 'BANNED', 'DISABLED'
            verificationStatus: 'VERIFIED', // 'VERIFIED', 'PENDING', 'UNVERIFIED', 'SUSPENDED'
            isLiveNow: true,
            capabilities: {
                live: true,
                video: true,
                chat: true,
                voice: true,
                selling: true
            },
            suspensionReason: '',
            banReason: '',
            banStartDate: '',
            banEndDate: '',
            createdAt: '2024-03-15'
        },
        {
            id: 'seller_al_omara_oud',
            slug: 'al-omara-oud',
            name: 'Al-Omara Royal Oud',
            storeName: 'Dar Al-Omara Perfumes',
            ownerEmail: 'contact@alomaraoud.com',
            rating: 4.9,
            reviewsCount: 418,
            followers: 28900,
            productsCount: 64,
            videosCount: 92,
            totalViews: 310500,
            ordersCount: 940,
            country: 'AE',
            status: 'ACTIVE',
            verificationStatus: 'VERIFIED',
            isLiveNow: false,
            capabilities: {
                live: true,
                video: true,
                chat: true,
                voice: true,
                selling: true
            },
            suspensionReason: '',
            banReason: '',
            banStartDate: '',
            banEndDate: '',
            createdAt: '2024-01-10'
        },
        {
            id: 'seller_najd_diamonds',
            slug: 'najd-diamonds',
            name: 'Najd High Jewelry',
            storeName: 'Najd Diamond Boutique',
            ownerEmail: 'sales@najd-diamonds.com',
            rating: 4.6,
            reviewsCount: 112,
            followers: 5400,
            productsCount: 38,
            videosCount: 29,
            totalViews: 64300,
            ordersCount: 88,
            country: 'SA',
            status: 'ACTIVE',
            verificationStatus: 'PENDING',
            isLiveNow: false,
            capabilities: {
                live: false, // Live disabled independently
                video: true,
                chat: true,
                voice: true,
                selling: true
            },
            suspensionReason: '',
            banReason: '',
            banStartDate: '',
            banEndDate: '',
            createdAt: '2024-05-20'
        },
        {
            id: 'seller_dubai_leather',
            slug: 'dubai-leather',
            name: 'Emirates Artisan Leather',
            storeName: 'Dubai Leather Workshop',
            ownerEmail: 'info@dubaileather.ae',
            rating: 3.2,
            reviewsCount: 45,
            followers: 1200,
            productsCount: 19,
            videosCount: 8,
            totalViews: 12400,
            ordersCount: 23,
            country: 'AE',
            status: 'SUSPENDED',
            verificationStatus: 'SUSPENDED',
            isLiveNow: false,
            capabilities: {
                live: false,
                video: false,
                chat: false,
                voice: false,
                selling: false
            },
            suspensionReason: 'Customer complaints regarding misleading product specifications and shipping delays.',
            banReason: '',
            banStartDate: '',
            banEndDate: '',
            createdAt: '2024-06-02'
        }
    ];

    const [sellers, setSellers] = useState(() => {
        try {
            const stored = localStorage.getItem('luxvono_admin_managed_sellers');
            if (stored) {
                return JSON.parse(stored);
            }
        } catch (_) {}
        return INITIAL_SELLERS;
    });

    const [reports, setReports] = useState(() => {
        try {
            const stored = localStorage.getItem('luxvono_moderation_reports');
            if (stored) {
                return JSON.parse(stored);
            }
        } catch (_) {}
        return [
            {
                id: 'rep_101',
                targetType: 'SELLER',
                targetId: 'seller_dubai_leather',
                targetTitle: 'Emirates Artisan Leather',
                reason: 'MISLEADING',
                notes: 'Product material claimed to be Italian calfskin but feels synthetic.',
                reporterId: 'user_cust_882',
                timestamp: new Date(Date.now() - 3600000 * 24).toISOString(),
                status: 'INVESTIGATING'
            },
            {
                id: 'rep_102',
                targetType: 'LIVE_STREAM',
                targetId: 'live_stream_99',
                targetTitle: 'Exclusive VIP Watch Auction',
                reason: 'SPAM',
                notes: 'Audience chat is flooded by spam bot comments.',
                reporterId: 'user_cust_441',
                timestamp: new Date(Date.now() - 3600000 * 2).toISOString(),
                status: 'RESOLVED'
            }
        ];
    });

    const [auditLogs, setAuditLogs] = useState(() => {
        try {
            const stored = localStorage.getItem('luxvono_moderation_audit_logs');
            if (stored) {
                return JSON.parse(stored);
            }
        } catch (_) {}
        return [
            {
                id: 'log_01',
                adminEmail: 'admin@luxvono.com',
                action: 'SUSPEND_SELLER',
                targetId: 'seller_dubai_leather',
                targetName: 'Emirates Artisan Leather',
                reason: 'Customer complaints regarding misleading product specifications.',
                timestamp: new Date(Date.now() - 3600000 * 48).toISOString()
            },
            {
                id: 'log_02',
                adminEmail: 'admin@luxvono.com',
                action: 'DISABLE_LIVE',
                targetId: 'seller_najd_diamonds',
                targetName: 'Najd High Jewelry',
                reason: 'Pending compliance check for high-value gemstone live auctions.',
                timestamp: new Date(Date.now() - 3600000 * 12).toISOString()
            }
        ];
    });

    // Modals
    const [selectedSeller, setSelectedSeller] = useState(null);
    const [modalAction, setModalAction] = useState(null); // 'SUSPEND' | 'BAN' | 'CAPABILITIES' | 'DETAILS'
    const [actionReason, setActionReason] = useState('');
    const [banStartDate, setBanStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [banEndDate, setBanEndDate] = useState('');
    const [feedbackBanner, setFeedbackBanner] = useState(null);

    // Save changes & broadcast to individual seller keys
    const persistSellers = (updatedSellers) => {
        setSellers(updatedSellers);
        try {
            localStorage.setItem('luxvono_admin_managed_sellers', JSON.stringify(updatedSellers));
            // Sync each seller's individual status key so storefront & seller portal instantly reflect changes
            updatedSellers.forEach(s => {
                const override = {
                    status: s.status,
                    verificationStatus: s.verificationStatus,
                    capabilities: s.capabilities,
                    suspensionReason: s.suspensionReason,
                    banReason: s.banReason,
                    banEndDate: s.banEndDate
                };
                localStorage.setItem(`luxvono_seller_status_${s.slug}`, JSON.stringify(override));
            });
        } catch (_) {}
    };

    const addAuditLog = (action, targetSeller, reason) => {
        const newLog = {
            id: `log_${Date.now()}`,
            adminEmail: user?.email || 'admin@luxvono.com',
            action,
            targetId: targetSeller.id,
            targetName: targetSeller.name,
            reason: reason || 'Administrative compliance review',
            timestamp: new Date().toISOString()
        };
        const updatedLogs = [newLog, ...auditLogs];
        setAuditLogs(updatedLogs);
        try {
            localStorage.setItem('luxvono_moderation_audit_logs', JSON.stringify(updatedLogs));
        } catch (_) {}
    };

    // Actions
    const handleSuspend = (e) => {
        e.preventDefault();
        if (!selectedSeller || !actionReason.trim()) return;

        const updated = sellers.map(s => {
            if (s.id === selectedSeller.id) {
                return {
                    ...s,
                    status: 'SUSPENDED',
                    verificationStatus: 'SUSPENDED',
                    suspensionReason: actionReason,
                    capabilities: {
                        ...s.capabilities,
                        live: false,
                        video: false,
                        chat: false,
                        voice: false,
                        selling: false
                    }
                };
            }
            return s;
        });

        persistSellers(updated);
        addAuditLog('SUSPEND_SELLER', selectedSeller, actionReason);
        setFeedbackBanner({ type: 'success', text: rtl ? `تم تعليق حساب ${selectedSeller.name} بنجاح.` : `Account for ${selectedSeller.name} has been suspended.` });
        setModalAction(null);
        setSelectedSeller(null);
        setActionReason('');
    };

    const handleBan = (e) => {
        e.preventDefault();
        if (!selectedSeller || !actionReason.trim()) return;

        const updated = sellers.map(s => {
            if (s.id === selectedSeller.id) {
                return {
                    ...s,
                    status: 'BANNED',
                    verificationStatus: 'SUSPENDED',
                    banReason: actionReason,
                    banStartDate,
                    banEndDate: banEndDate || null,
                    capabilities: {
                        live: false,
                        video: false,
                        chat: false,
                        voice: false,
                        selling: false
                    }
                };
            }
            return s;
        });

        persistSellers(updated);
        addAuditLog('BAN_SELLER', selectedSeller, `${actionReason} (Ends: ${banEndDate || 'Permanent'})`);
        setFeedbackBanner({ type: 'danger', text: rtl ? `تم حظر ${selectedSeller.name} نهائياً/مؤقتاً.` : `Seller ${selectedSeller.name} has been banned.` });
        setModalAction(null);
        setSelectedSeller(null);
        setActionReason('');
    };

    const handleRestore = (seller) => {
        const updated = sellers.map(s => {
            if (s.id === seller.id) {
                return {
                    ...s,
                    status: 'ACTIVE',
                    verificationStatus: 'VERIFIED',
                    suspensionReason: '',
                    banReason: '',
                    capabilities: {
                        live: true,
                        video: true,
                        chat: true,
                        voice: true,
                        selling: true
                    }
                };
            }
            return s;
        });

        persistSellers(updated);
        addAuditLog('RESTORE_SELLER', seller, 'Administrative restoration of seller privileges.');
        setFeedbackBanner({ type: 'success', text: rtl ? `تم استعادة وتفعيل حساب ${seller.name} بنجاح.` : `Account for ${seller.name} has been fully restored.` });
    };

    const handleToggleCapability = (seller, capabilityKey) => {
        const updated = sellers.map(s => {
            if (s.id === seller.id) {
                const currentVal = s.capabilities[capabilityKey];
                const newCapabilities = {
                    ...s.capabilities,
                    [capabilityKey]: !currentVal
                };
                return {
                    ...s,
                    capabilities: newCapabilities
                };
            }
            return s;
        });

        const actionName = `${seller.capabilities[capabilityKey] ? 'DISABLE' : 'ENABLE'}_${capabilityKey.toUpperCase()}`;
        persistSellers(updated);
        addAuditLog(actionName, seller, `Administrative switch of capability: ${capabilityKey}`);
    };

    const handleSetVerification = (seller, newStatus) => {
        const updated = sellers.map(s => {
            if (s.id === seller.id) {
                return { ...s, verificationStatus: newStatus };
            }
            return s;
        });

        persistSellers(updated);
        addAuditLog(`VERIFICATION_${newStatus}`, seller, `Admin updated verification status to ${newStatus}`);
    };

    // Filter sellers
    const filteredSellers = sellers.filter(s => {
        const matchesSearch = s.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                              s.storeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                              s.ownerEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
                              s.slug.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStatus = statusFilter === 'ALL' || s.status === statusFilter;
        const matchesVerification = verificationFilter === 'ALL' || s.verificationStatus === verificationFilter;
        return matchesSearch && matchesStatus && matchesVerification;
    });

    return (
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 20px', minHeight: '85vh', color: '#f8fafc' }}>
            {/* Top Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 16 }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                        <ShieldCheck size={28} color="#d4af37" />
                        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>
                            {rtl ? 'مركز التحكم وإدارة البائعين والمتاجر' : 'Admin Seller & Social Commerce Control Console'}
                        </h1>
                    </div>
                    <p style={{ margin: 0, fontSize: 13, color: '#94a3b8' }}>
                        {rtl 
                            ? 'إدارة صلاحيات البائعين المستقلين، تعليق الحسابات، حظر المخالفين، والتحكم في إمكانيات البث المباشر والمكالمات.' 
                            : 'Manage independent seller permissions, audit logs, feature capabilities, live stream locks, and customer safety reports.'}
                    </p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button
                        onClick={() => navigate('/seller/dashboard')}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#1e293b', border: '1px solid #334155', color: '#cbd5e1', padding: '10px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                    >
                        <Store size={16} />
                        <span>{rtl ? 'لوحة تحكم البائع' : 'Seller Portal'}</span>
                    </button>
                    <button
                        onClick={() => {
                            try {
                                localStorage.removeItem('luxvono_admin_managed_sellers');
                                setSellers(INITIAL_SELLERS);
                                setFeedbackBanner({ type: 'info', text: rtl ? 'تمت إعادة ضبط قائمة البائعين التجريبية' : 'Reset sample seller directory' });
                            } catch (_) {}
                        }}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.3)', color: '#d4af37', padding: '10px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                    >
                        <RefreshCw size={15} />
                        <span>{rtl ? 'تحديث السجلات' : 'Sync Records'}</span>
                    </button>
                </div>
            </div>

            {/* Notification Banner */}
            {feedbackBanner && (
                <div style={{ 
                    background: feedbackBanner.type === 'danger' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.15)', 
                    border: `1px solid ${feedbackBanner.type === 'danger' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(16, 185, 129, 0.3)'}`, 
                    borderRadius: 10, padding: '12px 18px', marginBottom: 22, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    color: feedbackBanner.type === 'danger' ? '#f87171' : '#34d399', fontSize: 13, fontWeight: 600 
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <CheckCircle2 size={18} />
                        <span>{feedbackBanner.text}</span>
                    </div>
                    <button onClick={() => setFeedbackBanner(null)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>
                        <X size={16} />
                    </button>
                </div>
            )}

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 12, borderBottom: '1px solid #334155', paddingBottom: 14, marginBottom: 24 }}>
                <button
                    onClick={() => setActiveTab('SELLERS')}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        background: activeTab === 'SELLERS' ? '#d4af37' : '#1e293b',
                        color: activeTab === 'SELLERS' ? '#0b0f19' : '#cbd5e1',
                        border: '1px solid',
                        borderColor: activeTab === 'SELLERS' ? '#d4af37' : '#334155',
                        padding: '10px 20px', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'all 0.2s'
                    }}
                >
                    <Users size={16} />
                    <span>{rtl ? `قائمة البائعين والمتاجر (${sellers.length})` : `Sellers & Stores (${sellers.length})`}</span>
                </button>

                <button
                    onClick={() => setActiveTab('REPORTS')}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        background: activeTab === 'REPORTS' ? '#d4af37' : '#1e293b',
                        color: activeTab === 'REPORTS' ? '#0b0f19' : '#cbd5e1',
                        border: '1px solid',
                        borderColor: activeTab === 'REPORTS' ? '#d4af37' : '#334155',
                        padding: '10px 20px', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'all 0.2s'
                    }}
                >
                    <ShieldAlert size={16} />
                    <span>{rtl ? `بلاغات المحتوى والأمان (${reports.length})` : `Safety Reports (${reports.length})`}</span>
                </button>

                <button
                    onClick={() => setActiveTab('AUDIT_LOGS')}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        background: activeTab === 'AUDIT_LOGS' ? '#d4af37' : '#1e293b',
                        color: activeTab === 'AUDIT_LOGS' ? '#0b0f19' : '#cbd5e1',
                        border: '1px solid',
                        borderColor: activeTab === 'AUDIT_LOGS' ? '#d4af37' : '#334155',
                        padding: '10px 20px', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'all 0.2s'
                    }}
                >
                    <FileText size={16} />
                    <span>{rtl ? `سجل الإجراءات الرقابي (${auditLogs.length})` : `Moderation Audit Logs (${auditLogs.length})`}</span>
                </button>
            </div>

            {/* TAB 1: SELLERS LIST & CONTROLS */}
            {activeTab === 'SELLERS' && (
                <div>
                    {/* Filters bar */}
                    <div style={{ display: 'flex', gap: 14, marginBottom: 20, flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 260, position: 'relative' }}>
                            <Search size={16} style={{ position: 'absolute', [rtl ? 'right' : 'left']: 14, top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />
                            <input
                                type="text"
                                placeholder={rtl ? 'بحث باسم البائع، المتجر، المعرف، أو البريد...' : 'Search by seller name, store, slug, or email...'}
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                style={{ width: '100%', padding: '10px 14px', [rtl ? 'paddingRight' : 'paddingLeft']: 40, background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#f8fafc', fontSize: 13, boxSizing: 'border-box' }}
                            />
                        </div>

                        <select
                            value={statusFilter}
                            onChange={e => setStatusFilter(e.target.value)}
                            style={{ padding: '10px 14px', background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#f8fafc', fontSize: 13, cursor: 'pointer' }}
                        >
                            <option value="ALL">{rtl ? 'كافة حالات الحساب' : 'All Account Statuses'}</option>
                            <option value="ACTIVE">{rtl ? 'نشط (ACTIVE)' : 'Active'}</option>
                            <option value="SUSPENDED">{rtl ? 'معلق (SUSPENDED)' : 'Suspended'}</option>
                            <option value="BANNED">{rtl ? 'محظور (BANNED)' : 'Banned'}</option>
                            <option value="PENDING">{rtl ? 'قيد المراجعة (PENDING)' : 'Pending Review'}</option>
                        </select>

                        <select
                            value={verificationFilter}
                            onChange={e => setVerificationFilter(e.target.value)}
                            style={{ padding: '10px 14px', background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#f8fafc', fontSize: 13, cursor: 'pointer' }}
                        >
                            <option value="ALL">{rtl ? 'كافة حالات التوثيق' : 'All Verification States'}</option>
                            <option value="VERIFIED">{rtl ? 'موثق رسمياً (VERIFIED)' : 'Verified Sellers'}</option>
                            <option value="PENDING">{rtl ? 'طلب توثيق معلق (PENDING)' : 'Pending Verification'}</option>
                            <option value="UNVERIFIED">{rtl ? 'غير موثق (UNVERIFIED)' : 'Unverified'}</option>
                            <option value="SUSPENDED">{rtl ? 'توثيق ملغى (SUSPENDED)' : 'Suspended Verification'}</option>
                        </select>
                    </div>

                    {/* Sellers Table */}
                    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, overflow: 'hidden' }}>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: rtl ? 'right' : 'left', fontSize: 13 }}>
                                <thead>
                                    <tr style={{ background: '#0b0f19', borderBottom: '1px solid #334155', color: '#94a3b8', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        <th style={{ padding: '14px 16px' }}>{rtl ? 'البائع والمتجر' : 'Seller / Store'}</th>
                                        <th style={{ padding: '14px 16px' }}>{rtl ? 'التقييم والمتابعون' : 'Rating & Followers'}</th>
                                        <th style={{ padding: '14px 16px' }}>{rtl ? 'المنتجات والمشاهدات' : 'Products & Views'}</th>
                                        <th style={{ padding: '14px 16px' }}>{rtl ? 'حالة الحساب' : 'Account Status'}</th>
                                        <th style={{ padding: '14px 16px' }}>{rtl ? 'التوثيق' : 'Verification'}</th>
                                        <th style={{ padding: '14px 16px' }}>{rtl ? 'تحكم الصلاحيات المباشرة' : 'Independent Capabilities'}</th>
                                        <th style={{ padding: '14px 16px', textAlign: 'center' }}>{rtl ? 'إجراءات الإشراف' : 'Moderation Actions'}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredSellers.map(seller => {
                                        const isSuspended = seller.status === 'SUSPENDED';
                                        const isBanned = seller.status === 'BANNED';

                                        return (
                                            <tr key={seller.id} style={{ borderBottom: '1px solid #334155', transition: 'background 0.15s' }}>
                                                {/* Seller / Store info */}
                                                <td style={{ padding: '16px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                        <div style={{ width: 42, height: 42, borderRadius: '50%', background: 'linear-gradient(135deg, #d4af37 0%, #aa8214 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0b0f19', fontWeight: 800, fontSize: 15, flexShrink: 0 }}>
                                                            {seller.name.charAt(0)}
                                                        </div>
                                                        <div>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                <strong style={{ color: '#f8fafc', fontSize: 14 }}>{seller.name}</strong>
                                                                {seller.isLiveNow && (
                                                                    <span style={{ background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
                                                                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />
                                                                        LIVE
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 2 }}>{seller.storeName}</div>
                                                            <div style={{ color: '#64748b', fontSize: 11, marginTop: 1 }}>{seller.ownerEmail}</div>
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* Rating & Followers */}
                                                <td style={{ padding: '16px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#f59e0b', fontWeight: 700 }}>
                                                        <Star size={14} fill="#f59e0b" />
                                                        <span>{seller.rating}</span>
                                                        <span style={{ color: '#64748b', fontSize: 11, fontWeight: 500 }}>({seller.reviewsCount})</span>
                                                    </div>
                                                    <div style={{ color: '#cbd5e1', fontSize: 12, marginTop: 4 }}>
                                                        {seller.followers.toLocaleString()} {rtl ? 'متابع' : 'followers'}
                                                    </div>
                                                </td>

                                                {/* Products & Views */}
                                                <td style={{ padding: '16px' }}>
                                                    <div style={{ color: '#cbd5e1', fontSize: 13, fontWeight: 600 }}>
                                                        {seller.productsCount} {rtl ? 'منتج' : 'products'} • {seller.videosCount} {rtl ? 'فيديو' : 'videos'}
                                                    </div>
                                                    <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>
                                                        {seller.totalViews.toLocaleString()} {rtl ? 'مشاهدة' : 'views'}
                                                    </div>
                                                </td>

                                                {/* Status Badge */}
                                                <td style={{ padding: '16px' }}>
                                                    <span style={{
                                                        padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, display: 'inline-block',
                                                        background: seller.status === 'ACTIVE' ? 'rgba(16, 185, 129, 0.15)' : 
                                                                    seller.status === 'SUSPENDED' ? 'rgba(245, 158, 11, 0.15)' :
                                                                    seller.status === 'BANNED' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(148, 163, 184, 0.15)',
                                                        color: seller.status === 'ACTIVE' ? '#34d399' :
                                                               seller.status === 'SUSPENDED' ? '#fbbf24' :
                                                               seller.status === 'BANNED' ? '#f87171' : '#94a3b8',
                                                        border: `1px solid ${
                                                            seller.status === 'ACTIVE' ? 'rgba(16, 185, 129, 0.3)' :
                                                            seller.status === 'SUSPENDED' ? 'rgba(245, 158, 11, 0.3)' :
                                                            seller.status === 'BANNED' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(148, 163, 184, 0.3)'
                                                        }`
                                                    }}>
                                                        {seller.status}
                                                    </span>
                                                    {seller.suspensionReason && (
                                                        <div style={{ fontSize: 10, color: '#fbbf24', marginTop: 4, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={seller.suspensionReason}>
                                                            {seller.suspensionReason}
                                                        </div>
                                                    )}
                                                    {seller.banReason && (
                                                        <div style={{ fontSize: 10, color: '#f87171', marginTop: 4, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={seller.banReason}>
                                                            {seller.banReason}
                                                        </div>
                                                    )}
                                                </td>

                                                {/* Verification Dropdown */}
                                                <td style={{ padding: '16px' }}>
                                                    <select
                                                        value={seller.verificationStatus}
                                                        onChange={e => handleSetVerification(seller, e.target.value)}
                                                        style={{
                                                            background: '#0b0f19', border: '1px solid #334155', borderRadius: 6,
                                                            color: seller.verificationStatus === 'VERIFIED' ? '#38bdf8' : '#cbd5e1',
                                                            fontSize: 11, fontWeight: 700, padding: '4px 8px', cursor: 'pointer'
                                                        }}
                                                    >
                                                        <option value="VERIFIED">{rtl ? '✓ موثق' : '✓ Verified'}</option>
                                                        <option value="PENDING">{rtl ? 'قيد المراجعة' : 'Pending'}</option>
                                                        <option value="UNVERIFIED">{rtl ? 'غير موثق' : 'Unverified'}</option>
                                                        <option value="SUSPENDED">{rtl ? 'معلق التوثيق' : 'Suspended'}</option>
                                                    </select>
                                                </td>

                                                {/* Independent Capability Toggles */}
                                                <td style={{ padding: '16px' }}>
                                                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                        {/* LIVE */}
                                                        <button
                                                            onClick={() => handleToggleCapability(seller, 'live')}
                                                            title={seller.capabilities.live ? 'Live Enabled (Click to Disable)' : 'Live Disabled (Click to Enable)'}
                                                            style={{
                                                                padding: '5px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                                                background: seller.capabilities.live ? 'rgba(239, 68, 68, 0.15)' : '#0b0f19',
                                                                color: seller.capabilities.live ? '#ef4444' : '#64748b',
                                                                border: `1px solid ${seller.capabilities.live ? 'rgba(239, 68, 68, 0.3)' : '#334155'}`
                                                            }}
                                                        >
                                                            <Radio size={12} style={{ display: 'inline', verticalAlign: '-1px', marginRight: 3 }} />
                                                            LIVE
                                                        </button>

                                                        {/* VIDEO */}
                                                        <button
                                                            onClick={() => handleToggleCapability(seller, 'video')}
                                                            title={seller.capabilities.video ? 'Video Upload Enabled' : 'Video Upload Disabled'}
                                                            style={{
                                                                padding: '5px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                                                background: seller.capabilities.video ? 'rgba(168, 85, 247, 0.15)' : '#0b0f19',
                                                                color: seller.capabilities.video ? '#c084fc' : '#64748b',
                                                                border: `1px solid ${seller.capabilities.video ? 'rgba(168, 85, 247, 0.3)' : '#334155'}`
                                                            }}
                                                        >
                                                            <Video size={12} style={{ display: 'inline', verticalAlign: '-1px', marginRight: 3 }} />
                                                            VIDEO
                                                        </button>

                                                        {/* CHAT */}
                                                        <button
                                                            onClick={() => handleToggleCapability(seller, 'chat')}
                                                            title={seller.capabilities.chat ? 'Customer Chat Enabled' : 'Customer Chat Disabled'}
                                                            style={{
                                                                padding: '5px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                                                background: seller.capabilities.chat ? 'rgba(56, 189, 248, 0.15)' : '#0b0f19',
                                                                color: seller.capabilities.chat ? '#38bdf8' : '#64748b',
                                                                border: `1px solid ${seller.capabilities.chat ? 'rgba(56, 189, 248, 0.3)' : '#334155'}`
                                                            }}
                                                        >
                                                            <MessageSquare size={12} style={{ display: 'inline', verticalAlign: '-1px', marginRight: 3 }} />
                                                            CHAT
                                                        </button>

                                                        {/* VOICE */}
                                                        <button
                                                            onClick={() => handleToggleCapability(seller, 'voice')}
                                                            title={seller.capabilities.voice ? 'Voice/Calls Enabled' : 'Voice/Calls Disabled'}
                                                            style={{
                                                                padding: '5px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                                                background: seller.capabilities.voice ? 'rgba(16, 185, 129, 0.15)' : '#0b0f19',
                                                                color: seller.capabilities.voice ? '#34d399' : '#64748b',
                                                                border: `1px solid ${seller.capabilities.voice ? 'rgba(16, 185, 129, 0.3)' : '#334155'}`
                                                            }}
                                                        >
                                                            <PhoneCall size={12} style={{ display: 'inline', verticalAlign: '-1px', marginRight: 3 }} />
                                                            CALLS
                                                        </button>

                                                        {/* SELLING */}
                                                        <button
                                                            onClick={() => handleToggleCapability(seller, 'selling')}
                                                            title={seller.capabilities.selling ? 'Publishing & Selling Enabled' : 'Selling Disabled'}
                                                            style={{
                                                                padding: '5px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                                                background: seller.capabilities.selling ? 'rgba(212, 175, 55, 0.15)' : '#0b0f19',
                                                                color: seller.capabilities.selling ? '#d4af37' : '#64748b',
                                                                border: `1px solid ${seller.capabilities.selling ? 'rgba(212, 175, 55, 0.3)' : '#334155'}`
                                                            }}
                                                        >
                                                            <ShoppingBag size={12} style={{ display: 'inline', verticalAlign: '-1px', marginRight: 3 }} />
                                                            SELL
                                                        </button>
                                                    </div>
                                                </td>

                                                {/* Actions */}
                                                <td style={{ padding: '16px', textAlign: 'center' }}>
                                                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                                                        {/* Public Profile Link */}
                                                        <a
                                                            href={`/store/${seller.slug}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title={rtl ? 'عرض الصفحة العامة للبائع' : 'View Public Store Profile'}
                                                            style={{ padding: '6px 8px', borderRadius: 6, background: '#0b0f19', border: '1px solid #334155', color: '#cbd5e1', cursor: 'pointer' }}
                                                        >
                                                            <ExternalLink size={14} />
                                                        </a>

                                                        {/* Suspend / Restore */}
                                                        {isSuspended || isBanned ? (
                                                            <button
                                                                onClick={() => handleRestore(seller)}
                                                                title={rtl ? 'استعادة وتفعيل الحساب' : 'Restore Account'}
                                                                style={{ padding: '6px 12px', borderRadius: 6, background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.3)', color: '#34d399', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                                                            >
                                                                <Unlock size={14} />
                                                                <span>{rtl ? 'استعادة' : 'Restore'}</span>
                                                            </button>
                                                        ) : (
                                                            <>
                                                                <button
                                                                    onClick={() => { setSelectedSeller(seller); setModalAction('SUSPEND'); }}
                                                                    title={rtl ? 'تعليق حساب البائع' : 'Suspend Seller'}
                                                                    style={{ padding: '6px 10px', borderRadius: 6, background: 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(245, 158, 11, 0.3)', color: '#fbbf24', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                                                                >
                                                                    <Lock size={14} />
                                                                    <span>{rtl ? 'تعليق' : 'Suspend'}</span>
                                                                </button>
                                                                <button
                                                                    onClick={() => { setSelectedSeller(seller); setModalAction('BAN'); }}
                                                                    title={rtl ? 'حظر الحساب' : 'Ban Seller'}
                                                                    style={{ padding: '6px 10px', borderRadius: 6, background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#f87171', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                                                                >
                                                                    <Ban size={14} />
                                                                    <span>{rtl ? 'حظر' : 'Ban'}</span>
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 2: REPORTS QUEUE */}
            {activeTab === 'REPORTS' && (
                <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, overflow: 'hidden' }}>
                    <div style={{ padding: '18px 24px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                            <h3 style={{ margin: '0 0 4px 0', fontSize: 16, fontWeight: 700 }}>
                                {rtl ? 'بلاغات المستخدمين وشكاوى المحتوى' : 'Incoming User Safety & Moderation Reports'}
                            </h3>
                            <span style={{ fontSize: 12, color: '#94a3b8' }}>
                                {rtl ? 'البلاغات المرسلة من العملاء ضد البائعين، البث المباشر، مقاطع الفيديو، أو الرسائل.' : 'Reports lodged against sellers, live broadcasts, product videos, or offensive messages.'}
                            </span>
                        </div>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: rtl ? 'right' : 'left', fontSize: 13 }}>
                            <thead>
                                <tr style={{ background: '#0b0f19', borderBottom: '1px solid #334155', color: '#94a3b8', fontSize: 12 }}>
                                    <th style={{ padding: '12px 16px' }}>{rtl ? 'نوع البلاغ والهدف' : 'Target & Type'}</th>
                                    <th style={{ padding: '12px 16px' }}>{rtl ? 'تصنيف المخالفة' : 'Violation Category'}</th>
                                    <th style={{ padding: '12px 16px' }}>{rtl ? 'تفاصيل الشكوى' : 'Report Details'}</th>
                                    <th style={{ padding: '12px 16px' }}>{rtl ? 'الوقت' : 'Timestamp'}</th>
                                    <th style={{ padding: '12px 16px' }}>{rtl ? 'الحالة' : 'Status'}</th>
                                    <th style={{ padding: '12px 16px', textAlign: 'center' }}>{rtl ? 'الإجراء' : 'Action'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reports.map(rep => (
                                    <tr key={rep.id} style={{ borderBottom: '1px solid #334155' }}>
                                        <td style={{ padding: '14px 16px' }}>
                                            <span style={{ background: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, display: 'inline-block', marginBottom: 4 }}>
                                                {rep.targetType}
                                            </span>
                                            <strong style={{ display: 'block', color: '#f8fafc' }}>{rep.targetTitle || rep.targetId}</strong>
                                        </td>
                                        <td style={{ padding: '14px 16px' }}>
                                            <span style={{ color: '#fbbf24', fontWeight: 600 }}>{rep.reason}</span>
                                        </td>
                                        <td style={{ padding: '14px 16px', maxWidth: 300, color: '#cbd5e1', fontSize: 12 }}>
                                            {rep.notes}
                                        </td>
                                        <td style={{ padding: '14px 16px', color: '#94a3b8', fontSize: 11 }}>
                                            {new Date(rep.timestamp).toLocaleString()}
                                        </td>
                                        <td style={{ padding: '14px 16px' }}>
                                            <span style={{
                                                padding: '3px 8px', borderRadius: 12, fontSize: 10, fontWeight: 700,
                                                background: rep.status === 'RESOLVED' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                                                color: rep.status === 'RESOLVED' ? '#34d399' : '#fbbf24'
                                            }}>
                                                {rep.status}
                                            </span>
                                        </td>
                                        <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                                            <button
                                                onClick={() => {
                                                    const updatedReports = reports.map(r => r.id === rep.id ? { ...r, status: 'RESOLVED' } : r);
                                                    setReports(updatedReports);
                                                    try {
                                                        localStorage.setItem('luxvono_moderation_reports', JSON.stringify(updatedReports));
                                                    } catch (_) {}
                                                }}
                                                style={{ padding: '6px 12px', background: '#0b0f19', border: '1px solid #334155', borderRadius: 6, color: '#34d399', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                                            >
                                                {rtl ? 'إغلاق البلاغ' : 'Resolve'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* TAB 3: AUDIT LOGS */}
            {activeTab === 'AUDIT_LOGS' && (
                <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, overflow: 'hidden' }}>
                    <div style={{ padding: '18px 24px', borderBottom: '1px solid #334155' }}>
                        <h3 style={{ margin: '0 0 4px 0', fontSize: 16, fontWeight: 700 }}>
                            {rtl ? 'سجل الرقابة الإدارية والعمليات غير القابل للتعديل' : 'Immutable Administrative Audit & Compliance Log'}
                        </h3>
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>
                            {rtl ? 'توثيق إلزامي لكافة عمليات التعليق والحظر وتغيير الصلاحيات المطبقة على البائعين.' : 'Comprehensive audit trail of all suspension, bans, capability restrictions, and restoration events.'}
                        </span>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: rtl ? 'right' : 'left', fontSize: 13 }}>
                            <thead>
                                <tr style={{ background: '#0b0f19', borderBottom: '1px solid #334155', color: '#94a3b8', fontSize: 12 }}>
                                    <th style={{ padding: '12px 16px' }}>{rtl ? 'المشرف الإداري' : 'Admin User'}</th>
                                    <th style={{ padding: '12px 16px' }}>{rtl ? 'نوع الإجراء' : 'Action Taken'}</th>
                                    <th style={{ padding: '12px 16px' }}>{rtl ? 'البائع المستهدف' : 'Target Seller'}</th>
                                    <th style={{ padding: '12px 16px' }}>{rtl ? 'السبب الموجب' : 'Reason / Justification'}</th>
                                    <th style={{ padding: '12px 16px' }}>{rtl ? 'التوقيت' : 'Timestamp'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {auditLogs.map(log => (
                                    <tr key={log.id} style={{ borderBottom: '1px solid #334155' }}>
                                        <td style={{ padding: '14px 16px', color: '#38bdf8', fontWeight: 600 }}>{log.adminEmail}</td>
                                        <td style={{ padding: '14px 16px' }}>
                                            <span style={{
                                                padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 800,
                                                background: log.action.includes('SUSPEND') || log.action.includes('BAN') ? 'rgba(239, 68, 68, 0.15)' : 'rgba(56, 189, 248, 0.15)',
                                                color: log.action.includes('SUSPEND') || log.action.includes('BAN') ? '#f87171' : '#38bdf8'
                                            }}>
                                                {log.action}
                                            </span>
                                        </td>
                                        <td style={{ padding: '14px 16px', color: '#f8fafc', fontWeight: 600 }}>{log.targetName}</td>
                                        <td style={{ padding: '14px 16px', color: '#cbd5e1', fontSize: 12 }}>{log.reason}</td>
                                        <td style={{ padding: '14px 16px', color: '#94a3b8', fontSize: 11 }}>{new Date(log.timestamp).toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* MODAL: SUSPEND SELLER */}
            {modalAction === 'SUSPEND' && selectedSeller && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(5px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
                    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 16, width: '100%', maxWidth: 520, padding: 28, boxSizing: 'border-box' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                            <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(245, 158, 11, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fbbf24' }}>
                                <AlertTriangle size={22} />
                            </div>
                            <div>
                                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#f8fafc' }}>
                                    {rtl ? `تعليق حساب البائع: ${selectedSeller.name}` : `Suspend Seller: ${selectedSeller.name}`}
                                </h3>
                                <span style={{ fontSize: 12, color: '#94a3b8' }}>
                                    {rtl ? 'سيتم تعطيل إمكانيات البث، النشر، والرسائل فورا مع إبقاء الطلبات القائمة سارية.' : 'Live broadcasting, video publishing, and chat will be immediately frozen.'}
                                </span>
                            </div>
                        </div>

                        <form onSubmit={handleSuspend}>
                            <div style={{ marginBottom: 18 }}>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#cbd5e1', marginBottom: 6 }}>
                                    {rtl ? 'سبب التعليق الإداري (إلزامي):' : 'Suspension Reason (Required):'}
                                </label>
                                <textarea
                                    required
                                    rows={4}
                                    placeholder={rtl ? 'اذكر سبب التعليق بالتفصيل لمشاركته مع البائع وحفظه في السجل الرقابي...' : 'Specify the violation details and policy reason...'}
                                    value={actionReason}
                                    onChange={e => setActionReason(e.target.value)}
                                    style={{ width: '100%', padding: '10px 14px', background: '#0b0f19', border: '1px solid #334155', borderRadius: 8, color: '#f8fafc', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
                                />
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                                <button
                                    type="button"
                                    onClick={() => { setModalAction(null); setSelectedSeller(null); }}
                                    style={{ padding: '10px 18px', background: '#0b0f19', border: '1px solid #334155', color: '#cbd5e1', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                                >
                                    {rtl ? 'إلغاء' : 'Cancel'}
                                </button>
                                <button
                                    type="submit"
                                    disabled={!actionReason.trim()}
                                    style={{ padding: '10px 22px', background: '#f59e0b', color: '#0b0f19', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: actionReason.trim() ? 'pointer' : 'not-allowed', opacity: actionReason.trim() ? 1 : 0.6 }}
                                >
                                    {rtl ? 'تأكيد التعليق الفوري' : 'Confirm Suspension'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL: BAN SELLER */}
            {modalAction === 'BAN' && selectedSeller && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(5px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
                    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 16, width: '100%', maxWidth: 520, padding: 28, boxSizing: 'border-box' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                            <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(239, 68, 68, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444' }}>
                                <Ban size={22} />
                            </div>
                            <div>
                                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#f8fafc' }}>
                                    {rtl ? `حظر الحساب التجاري: ${selectedSeller.name}` : `Ban Seller: ${selectedSeller.name}`}
                                </h3>
                                <span style={{ fontSize: 12, color: '#94a3b8' }}>
                                    {rtl ? 'حظر شامل يمنع تسجيل الدخول واستخدام كافة أدوات المنصة.' : 'Full account ban blocking access to all seller tools.'}
                                </span>
                            </div>
                        </div>

                        <form onSubmit={handleBan}>
                            <div style={{ marginBottom: 14 }}>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#cbd5e1', marginBottom: 6 }}>
                                    {rtl ? 'سبب الحظر (إلزامي):' : 'Ban Reason (Required):'}
                                </label>
                                <textarea
                                    required
                                    rows={3}
                                    placeholder={rtl ? 'مخالفة جسيمة لشروط وأحكام منصة LUXVONO...' : 'Specify reasons for account ban...'}
                                    value={actionReason}
                                    onChange={e => setActionReason(e.target.value)}
                                    style={{ width: '100%', padding: '10px 14px', background: '#0b0f19', border: '1px solid #334155', borderRadius: 8, color: '#f8fafc', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 4 }}>
                                        {rtl ? 'تاريخ البدء' : 'Start Date'}
                                    </label>
                                    <input
                                        type="date"
                                        value={banStartDate}
                                        onChange={e => setBanStartDate(e.target.value)}
                                        style={{ width: '100%', padding: '8px 12px', background: '#0b0f19', border: '1px solid #334155', borderRadius: 6, color: '#f8fafc', fontSize: 12, boxSizing: 'border-box' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 4 }}>
                                        {rtl ? 'تاريخ الانتهاء (اتركه فارغاً للحظر الدائم)' : 'End Date (Empty for Permanent)'}
                                    </label>
                                    <input
                                        type="date"
                                        value={banEndDate}
                                        onChange={e => setBanEndDate(e.target.value)}
                                        style={{ width: '100%', padding: '8px 12px', background: '#0b0f19', border: '1px solid #334155', borderRadius: 6, color: '#f8fafc', fontSize: 12, boxSizing: 'border-box' }}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                                <button
                                    type="button"
                                    onClick={() => { setModalAction(null); setSelectedSeller(null); }}
                                    style={{ padding: '10px 18px', background: '#0b0f19', border: '1px solid #334155', color: '#cbd5e1', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                                >
                                    {rtl ? 'إلغاء' : 'Cancel'}
                                </button>
                                <button
                                    type="submit"
                                    disabled={!actionReason.trim()}
                                    style={{ padding: '10px 22px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: actionReason.trim() ? 'pointer' : 'not-allowed', opacity: actionReason.trim() ? 1 : 0.6 }}
                                >
                                    {rtl ? 'تنفيذ الحظر الآن' : 'Execute Ban'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
